import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import {
  WaveCRDTBridge,
  WaveBlockKind,
  WaveStatus,
  backfillMissingBlockAuthors,
  repairOwnerBlockAuthorNames,
  editorsOnBlock,
  createDefaultBlock,
  hasAgentResponseChild,
  isSameParticipantSub,
  participantRecordsForTodoMentions,
  resolveOwnerAuthorName,
  runningAgentTaskBlocks,
  shouldReconcileAgentTask,
  shouldBackfillWhaleAgentQuestion,
  type WaveBlipRecord,
  type WaveCommentRecord,
  type WaveDocEditor,
  type WaveOpRecord,
  type WavePresenceRecord,
  type WaveLastSeenRecord,
  type WaveCoverKeys,
  type WaveRecord,
  type WaveDocumentSnapshot,
  type WaveThreadView,
  type WaveTimelineOrder,
  partitionArchivedBlocks,
  isIndexableMime,
  nestedChildCountByParent,
  buildThreadToc,
  projectThreadFeed,
  type WaveTocEntry,
} from "@vida/wave-core";
import { WaveBlockView } from "../blocks/WaveBlockView";
import { WaveBlipThread } from "./WaveBlipThread";
import { WaveCommentThread } from "./WaveCommentThread";
import { WaveEditorHeaderBar } from "./WaveEditorHeaderBar";
import { WaveArchivedWidgetsPanel } from "./WaveArchivedWidgetsPanel";
import { WaveTimelineComposer } from "./WaveTimelineComposer";
import { WaveThreadToc } from "./WaveThreadToc";
import { WaveTimelineFeed, resolveFeedAuthor } from "./WaveTimelineFeed";
import { WaveWidgetPicker } from "./WaveWidgetPicker";
import type { WaveFeedDensity } from "./waveFeedTokens";
import { WavePlaybackView,
  reconstructForPlayback,
} from "./WavePlaybackView";
import { runWaveAssistant, trackActivity } from "../../services/waveSession";
import { showWaveNotification } from "../../services/notifications";
import { currentUserEmail, currentUserSub } from "../../auth";
import { useWaveEditingPresence } from "../../hooks/useWaveEditingPresence";
import { useWaveTocActiveBlock } from "../../hooks/useWaveTocActiveBlock";
import { uploadBlobFile, inferBlobKind, normalizedContentType } from "../../services/waveBlobUpload";
import { filesFromDataTransfer } from "../../services/waveClipboardImages";
import {
  resolveGalleryBlockId,
  uploadImagesToGalleryBlock,
} from "../../services/waveGalleryUpload";
import { runWaveDocQueryFromDirective } from "../../services/waveDocQuery";
import { useWaveTodoMirror } from "../../services/waveTodoMirror";
import {
  WaveAnalystPanel,
  type WaveAnalystTarget,
} from "./WaveAnalystPanel";
import { WaveWhalePanel, type WaveWhaleTarget } from "./WaveWhalePanel";
import { reconcileWhaleTask, backfillWhaleAgentQuestion, AGENT_QUESTION_BACKFILL_LIMIT } from "../../services/waveWhale";
import { whaleTargetFromBlock } from "../../services/waveWhaleTarget";
import {
  hasCollapsedBlocksStorage,
  mergeNestedCollapsedDefaults,
  nestedBlockIdsForParent,
  readCollapsedBlocks,
  writeCollapsedBlocks,
} from "../../services/waveCollapseStorage";

const TEXT_SYNC_DEBOUNCE_MS = 50;
const THREAD_VIEW_STORAGE_KEY = "wave.thread.view";
const TIMELINE_ORDER_STORAGE_KEY = "wave.timeline.order";
const TOC_VISIBLE_STORAGE_KEY = "wave.tocVisible";
const TOC_FLASH_CLASS = "wave-feed-anchor--toc-flash";
const TOC_FLASH_MS = 1200;

function readTocVisible(): boolean {
  try {
    return localStorage.getItem(TOC_VISIBLE_STORAGE_KEY) !== "0";
  } catch {
    return true;
  }
}

function nestedHiddenStorageKey(waveId: string): string {
  return `wave.nestedHidden.${waveId}`;
}

function readNestedHidden(waveId: string): Set<string> {
  try {
    const raw = localStorage.getItem(nestedHiddenStorageKey(waveId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === "string"));
  } catch {
    return new Set();
  }
}

function writeNestedHidden(waveId: string, ids: Set<string>): void {
  try {
    localStorage.setItem(nestedHiddenStorageKey(waveId), JSON.stringify([...ids]));
  } catch {
    // ignore quota / private mode
  }
}

function readThreadView(): WaveThreadView {
  try {
    const raw = localStorage.getItem(THREAD_VIEW_STORAGE_KEY);
    return raw === "story" ? "story" : "timeline";
  } catch {
    return "timeline";
  }
}

function readTimelineOrder(): WaveTimelineOrder {
  try {
    const raw = localStorage.getItem(TIMELINE_ORDER_STORAGE_KEY);
    return raw === "desc" ? "desc" : "asc";
  } catch {
    return "asc";
  }
}

function resolveDocQueryParentFile(
  blocks: import("@vida/wave-core").WaveBlock[],
): string | null {
  const files = blocks.filter(
    (b) => b.kind === WaveBlockKind.File && b.payload.s3Key?.trim(),
  );
  return files.at(-1)?.id ?? null;
}

function stampBlockAuthor(
  editor: WaveDocEditor,
  blockId: string,
  sub: string,
  participants: WaveRecord["participants"],
): void {
  const name = resolveOwnerAuthorName(sub, participants, currentUserEmail());
  editor.setMetaString(blockId, "authorSub", sub);
  editor.setMetaString(blockId, "authorName", name);
}

type Props = {
  waveId: string;
  wave: WaveRecord;
  bridge: WaveCRDTBridge;
  editor?: WaveDocEditor | null;
  documentRevision?: number;
  legacyBlips?: WaveBlipRecord[];
  presences: WavePresenceRecord[];
  lastSeen?: WaveLastSeenRecord[];
  playbackBaseline: WaveDocumentSnapshot;
  playbackOps: WaveOpRecord[];
  onEmitOps: (ops: string[]) => void;
  onPostComment?: (
    anchor: string,
    body: string,
    parentId: string | null,
    galleryItemId?: string | null,
  ) => void;
  onToggleReaction?: (commentId: string, emoji: string) => void;
  onPostLegacyBlip?: (anchor: string, body: string) => void;
  onStatusChange: (status: WaveStatus) => void;
  onArchiveToggle?: () => void;
  onRenameTitle?: () => void;
  onPublishPresence: (
    anchor: string | null,
    cursorOffset?: number | null,
    cursorEndOffset?: number | null,
  ) => void;
  onRefreshPlayback: () => Promise<void>;
  onPlaybackBegin?: () => void;
  onCoverKeyChange?: (waveId: string, keys: WaveCoverKeys) => void;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  onInvite?: () => void;
  onFlushDocumentSync?: () => void | Promise<void>;
  onBack?: () => void;
  backLabel?: string;
  sidebarCollapsed?: boolean;
  onReopenSidebar?: () => void;
};

export function WaveEditor({
  waveId,
  wave,
  bridge,
  editor = null,
  documentRevision = 0,
  legacyBlips = [],
  presences,
  lastSeen = [],
  playbackBaseline,
  playbackOps,
  onEmitOps,
  onPostComment,
  onToggleReaction,
  onPostLegacyBlip,
  onStatusChange,
  onArchiveToggle,
  onRenameTitle,
  onPublishPresence,
  onRefreshPlayback,
  onPlaybackBegin,
  onCoverKeyChange,
  isFavorite = false,
  onToggleFavorite,
  onInvite,
  onFlushDocumentSync,
  onBack,
  backLabel,
  sidebarCollapsed = false,
  onReopenSidebar,
}: Props) {
  const userSub = currentUserSub();
  const sessionEmail = currentUserEmail();

  const syncAfterYjsEdit = useCallback(() => {
    void onFlushDocumentSync?.();
  }, [onFlushDocumentSync]);
  const feedAuthorContext = useMemo(
    () => ({
      ownerSub: wave.ownerSub,
      sessionSub: userSub,
      sessionEmail,
    }),
    [wave.ownerSub, userSub, sessionEmail],
  );

  const { beginEditing, endEditing, publishCursor, touchTypingActivity } =
    useWaveEditingPresence(onPublishPresence);
  const feedDensity: WaveFeedDensity = "comfortable";
  const [commandText, setCommandText] = useState("");
  const [selectedBlipBlockId, setSelectedBlipBlockId] = useState<string | null>(
    null,
  );
  const [blipDraft, setBlipDraft] = useState("");
  const [showPlayback, setShowPlayback] = useState(false);
  const [playbackSeq, setPlaybackSeq] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [docQueryBusy, setDocQueryBusy] = useState(false);
  const [threadView, setThreadView] = useState<WaveThreadView>(() => readThreadView());
  const [timelineOrder, setTimelineOrder] = useState<WaveTimelineOrder>(() =>
    readTimelineOrder(),
  );
  const [insertAfterBlockId, setInsertAfterBlockId] = useState<string | null>(null);
  const [inlinePickerOpen, setInlinePickerOpen] = useState(false);
  const [archivedPanelOpen, setArchivedPanelOpen] = useState(false);
  const [analystTarget, setAnalystTarget] = useState<WaveAnalystTarget | null>(null);
  const [analystOpen, setAnalystOpen] = useState(false);
  const [whaleTarget, setWhaleTarget] = useState<WaveWhaleTarget | null>(null);
  const [whaleOpen, setWhaleOpen] = useState(false);
  const [collapsedBlocks, setCollapsedBlocks] = useState<Set<string>>(() => new Set());
  const [nestedHiddenBlocks, setNestedHiddenBlocks] = useState<Set<string>>(() =>
    readNestedHidden(waveId),
  );
  /** Blocks where the user closed the comments panel (while comments exist). */
  const [commentsCollapsedBlocks, setCommentsCollapsedBlocks] = useState<Set<string>>(
    () => new Set(),
  );
  const [tocVisible, setTocVisible] = useState<boolean>(() => readTocVisible());
  const textSyncTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRootRef = useRef<HTMLDivElement>(null);

  const playbackMaxSeq = Math.max(
    wave.nextOpSeq,
    ...playbackOps.map((o) => o.seq),
    0,
  );

  const liveBlocks = editor?.blocks ?? bridge.blocks;
  const { active: activeBlocks, archived: archivedBlocks } = useMemo(
    () => partitionArchivedBlocks(liveBlocks),
    [liveBlocks],
  );
  const liveComments: WaveCommentRecord[] = editor?.comments ?? [];
  const contentRevision = editor ? documentRevision : bridge.revision;
  const usesYjsComments = editor != null && onPostComment != null;

  useWaveTodoMirror(waveId, wave.spaceId, activeBlocks, Boolean(editor) && !showPlayback);

  const mentionParticipants = useMemo(
    () =>
      participantRecordsForTodoMentions({
        participants: wave.participants,
        presences,
        lastSeen,
        currentSub: userSub,
        ownerSub: wave.ownerSub,
        currentUserEmail: sessionEmail,
      }),
    [wave.participants, wave.ownerSub, presences, lastSeen, userSub, sessionEmail],
  );

  const displayedBlocks = showPlayback
    ? reconstructForPlayback(bridge, playbackBaseline, playbackOps, playbackSeq)
    : activeBlocks;

  useEffect(() => {
    setNestedHiddenBlocks(readNestedHidden(waveId));
    if (hasCollapsedBlocksStorage(waveId)) {
      setCollapsedBlocks(readCollapsedBlocks(waveId));
    } else {
      setCollapsedBlocks(new Set());
    }
  }, [waveId]);

  useEffect(() => {
    if (showPlayback || hasCollapsedBlocksStorage(waveId)) return;
    setCollapsedBlocks((prev) => mergeNestedCollapsedDefaults(prev, activeBlocks));
  }, [waveId, activeBlocks, showPlayback]);

  const toggleBlockCollapse = useCallback((blockId: string) => {
    setCollapsedBlocks((prev) => {
      const next = new Set(prev);
      if (next.has(blockId)) next.delete(blockId);
      else next.add(blockId);
      writeCollapsedBlocks(waveId, next);
      return next;
    });
  }, [waveId]);

  const topLevelBlockIds = useMemo(
    () => displayedBlocks.filter((b) => !b.payload.parentId).map((b) => b.id),
    [displayedBlocks],
  );

  const nestedCounts = useMemo(
    () => nestedChildCountByParent(displayedBlocks),
    [displayedBlocks],
  );

  const topLevelWithChildrenIds = useMemo(
    () => topLevelBlockIds.filter((id) => (nestedCounts.get(id) ?? 0) > 0),
    [topLevelBlockIds, nestedCounts],
  );

  const allTopLevelCollapsed = useMemo(
    () =>
      topLevelBlockIds.length > 0 &&
      topLevelBlockIds.every((id) => collapsedBlocks.has(id)) &&
      topLevelWithChildrenIds.every((id) => nestedHiddenBlocks.has(id)),
    [topLevelBlockIds, topLevelWithChildrenIds, collapsedBlocks, nestedHiddenBlocks],
  );

  const toggleNestedHidden = useCallback(
    (topId: string) => {
      setNestedHiddenBlocks((prev) => {
        const next = new Set(prev);
        if (next.has(topId)) next.delete(topId);
        else next.add(topId);
        writeNestedHidden(waveId, next);
        return next;
      });
    },
    [waveId],
  );

  const handleToggleCollapseAll = useCallback(() => {
    if (allTopLevelCollapsed) {
      const next = new Set<string>();
      setCollapsedBlocks(next);
      writeCollapsedBlocks(waveId, next);
      setNestedHiddenBlocks(new Set());
      writeNestedHidden(waveId, new Set());
      return;
    }
    const next = new Set(topLevelBlockIds);
    setCollapsedBlocks(next);
    writeCollapsedBlocks(waveId, next);
    const hidden = new Set(topLevelWithChildrenIds);
    setNestedHiddenBlocks(hidden);
    writeNestedHidden(waveId, hidden);
  }, [allTopLevelCollapsed, topLevelBlockIds, topLevelWithChildrenIds, waveId]);

  const toggleCollapseAllNested = useCallback(
    (parentId: string) => {
      const childIds = nestedBlockIdsForParent(displayedBlocks, parentId);
      if (childIds.length === 0) return;
      setCollapsedBlocks((prev) => {
        const allCollapsed = childIds.every((id) => prev.has(id));
        const next = new Set(prev);
        if (allCollapsed) {
          for (const id of childIds) next.delete(id);
        } else {
          for (const id of childIds) next.add(id);
        }
        writeCollapsedBlocks(waveId, next);
        return next;
      });
    },
    [displayedBlocks, waveId],
  );

  const effectiveThreadView: WaveThreadView = showPlayback ? "timeline" : threadView;
  const effectiveTimelineOrder = showPlayback ? "asc" : timelineOrder;

  // Quota della toolbar sticky esposta come CSS var: usata dal TOC (top sticky)
  // e dallo scroll-margin delle righe feed (compensazione senza math JS).
  useEffect(() => {
    const rootEl = editorRootRef.current;
    if (!rootEl || typeof ResizeObserver === "undefined") return;
    const toolbar = rootEl.querySelector(".wave-editor-toolbar");
    if (!(toolbar instanceof HTMLElement)) return;
    const update = () => {
      rootEl.style.setProperty(
        "--wave-editor-toolbar-h",
        `${toolbar.offsetHeight}px`,
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(toolbar);
    return () => observer.disconnect();
  }, [showPlayback]);

  const tocEntries = useMemo(
    () =>
      buildThreadToc(
        projectThreadFeed(displayedBlocks, effectiveThreadView, effectiveTimelineOrder),
      ),
    [displayedBlocks, effectiveThreadView, effectiveTimelineOrder],
  );

  const tocDimmedIds = useMemo(() => {
    const dimmed = new Set<string>();
    for (const entry of tocEntries) {
      if (entry.parentId && nestedHiddenBlocks.has(entry.parentId)) {
        dimmed.add(entry.blockId);
      }
    }
    return dimmed;
  }, [tocEntries, nestedHiddenBlocks]);

  const showToc = !showPlayback && tocVisible && tocEntries.length > 0;
  const { activeBlockId: activeTocBlockId, setActiveBlockId: setActiveTocBlockId, suspendSpy } =
    useWaveTocActiveBlock(tocEntries, editorRootRef, {
      enabled: showToc,
      // Le righe con padre nascosto escono dal DOM: ri-agganciare l'observer.
      rebindKey: [...nestedHiddenBlocks].sort().join(","),
    });

  const handleToggleToc = useCallback(() => {
    setTocVisible((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(TOC_VISIBLE_STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const handleTocSelect = useCallback(
    (entry: WaveTocEntry) => {
      const parentId = entry.parentId;
      if (parentId && nestedHiddenBlocks.has(parentId)) {
        setNestedHiddenBlocks((prev) => {
          const next = new Set(prev);
          next.delete(parentId);
          writeNestedHidden(waveId, next);
          return next;
        });
      }
      if (collapsedBlocks.has(entry.blockId)) {
        setCollapsedBlocks((prev) => {
          const next = new Set(prev);
          next.delete(entry.blockId);
          writeCollapsedBlocks(waveId, next);
          return next;
        });
      }
      suspendSpy(800);
      setActiveTocBlockId(entry.blockId);
      // Doppio rAF: la riga può dover rimontare dopo le espansioni sopra.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = editorRootRef.current?.querySelector(
            `[data-block-id="${CSS.escape(entry.blockId)}"]`,
          );
          if (!(el instanceof HTMLElement)) return;
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          el.classList.add(TOC_FLASH_CLASS);
          window.setTimeout(() => el.classList.remove(TOC_FLASH_CLASS), TOC_FLASH_MS);
        });
      });
    },
    [nestedHiddenBlocks, collapsedBlocks, waveId, suspendSpy, setActiveTocBlockId],
  );

  const handleArchiveBlock = useCallback(
    (blockId: string) => {
      if (!editor || showPlayback) return;
      editor.archiveBlock(blockId);
      if (selectedBlipBlockId === blockId) setSelectedBlipBlockId(null);
      void syncAfterYjsEdit();
    },
    [editor, showPlayback, selectedBlipBlockId, syncAfterYjsEdit],
  );

  const handleOpenAnalyst = useCallback(
    (block: import("@vida/wave-core").WaveBlock) => {
      const s3Key = block.payload.s3Key?.trim();
      const fileName = block.payload.fileName?.trim();
      if (block.kind === WaveBlockKind.Text && block.payload.text?.trim()) {
        setAnalystTarget({
          blockId: block.id,
          sourceKind: "text",
          label: fileName || "Widget testo",
          textContent: block.payload.text,
        });
        setAnalystOpen(true);
        return;
      }
      if (!s3Key || !fileName || block.payload.ragStatus !== "indexed") return;
      setAnalystTarget({
        blockId: block.id,
        sourceKind: "file",
        s3Key,
        fileName,
      });
      setAnalystOpen(true);
    },
    [],
  );

  const handleAskWhale = useCallback((block: import("@vida/wave-core").WaveBlock) => {
    setWhaleTarget(whaleTargetFromBlock(block));
    setWhaleOpen(true);
  }, []);

  useEffect(() => {
    if (!editor || showPlayback) return;
    let cancelled = false;
    void (async () => {
      const blocks = editor.blocks;
      for (const task of runningAgentTaskBlocks(blocks)) {
        if (cancelled) return;
        if (!shouldReconcileAgentTask(task, userSub)) continue;
        const parentId = task.payload.parentId?.trim();
        if (!parentId) continue;
        if (
          task.payload.agentStatus === "done" ||
          hasAgentResponseChild(blocks, parentId)
        ) {
          continue;
        }
        const parent = blocks.find((b) => b.id === parentId);
        if (!parent) continue;
        try {
          await reconcileWhaleTask({
            waveId,
            parentBlock: parent,
            taskBlock: task,
            editor,
          });
          if (!cancelled) await syncAfterYjsEdit();
        } catch {
          /* best effort */
        }
      }

      let backfillCount = 0;
      for (const task of blocks) {
        if (cancelled) return;
        if (backfillCount >= AGENT_QUESTION_BACKFILL_LIMIT) break;
        if (!shouldBackfillWhaleAgentQuestion(task, userSub)) continue;
        try {
          const ok = await backfillWhaleAgentQuestion({ editor, taskBlock: task });
          if (ok) {
            backfillCount += 1;
            if (!cancelled) await syncAfterYjsEdit();
          }
        } catch {
          /* best effort */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editor, showPlayback, userSub, waveId, documentRevision, syncAfterYjsEdit]);

  const handleRestoreBlock = useCallback(
    (blockId: string) => {
      if (!editor || showPlayback) return;
      editor.restoreBlock(blockId);
      void syncAfterYjsEdit();
    },
    [editor, showPlayback, syncAfterYjsEdit],
  );

  const handleDeleteBlockForever = useCallback(
    (blockId: string) => {
      if (!editor || showPlayback) return;
      editor.removeBlock(blockId);
      if (selectedBlipBlockId === blockId) setSelectedBlipBlockId(null);
      void syncAfterYjsEdit();
    },
    [editor, showPlayback, selectedBlipBlockId, syncAfterYjsEdit],
  );

  const resolveInsertAnchor = useCallback((): string | null | undefined => {
    if (effectiveThreadView === "story" && insertAfterBlockId != null) {
      return insertAfterBlockId.length > 0 ? insertAfterBlockId : undefined;
    }
    return activeBlocks.at(-1)?.id ?? undefined;
  }, [effectiveThreadView, insertAfterBlockId, activeBlocks]);

  const clearInlineInsert = useCallback(() => {
    setInsertAfterBlockId(null);
    setInlinePickerOpen(false);
  }, []);

  const handleThreadViewChange = useCallback((view: WaveThreadView) => {
    setThreadView(view);
    try {
      localStorage.setItem(THREAD_VIEW_STORAGE_KEY, view);
    } catch {
      /* ignore */
    }
    clearInlineInsert();
  }, [clearInlineInsert]);

  const handleTimelineOrderToggle = useCallback(() => {
    setTimelineOrder((prev) => {
      const next = prev === "asc" ? "desc" : "asc";
      try {
        localStorage.setItem(TIMELINE_ORDER_STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const openInlineInsert = useCallback((afterBlockId: string) => {
    if (effectiveThreadView !== "story" || showPlayback) return;
    setInsertAfterBlockId(afterBlockId);
    setInlinePickerOpen(true);
  }, [effectiveThreadView, showPlayback]);

  useEffect(() => {
    if (!inlinePickerOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") clearInlineInsert();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [inlinePickerOpen, clearInlineInsert]);

  useEffect(() => {
    if (!editor || showPlayback) return;
    if (!isSameParticipantSub(userSub, wave.ownerSub)) return;
    const ownerAuthorName = resolveOwnerAuthorName(
      wave.ownerSub,
      wave.participants,
      sessionEmail,
    );
    const ctx = {
      ownerSub: wave.ownerSub,
      currentUserSub: userSub,
      ownerAuthorName,
    };
    backfillMissingBlockAuthors(liveBlocks, editor, ctx);
    repairOwnerBlockAuthorNames(liveBlocks, editor, ctx);
  }, [
    editor,
    showPlayback,
    userSub,
    wave.ownerSub,
    wave.participants,
    sessionEmail,
    documentRevision,
    liveBlocks,
  ]);

  const commentCountByBlock = useMemo(() => {
    const counts = new Map<string, number>();
    for (const comment of liveComments) {
      if (!comment.blockAnchorId) continue;
      counts.set(
        comment.blockAnchorId,
        (counts.get(comment.blockAnchorId) ?? 0) + 1,
      );
    }
    return counts;
  }, [liveComments]);

  const legacyBlipCountByBlock = useMemo(() => {
    const counts = new Map<string, number>();
    for (const blip of legacyBlips) {
      if (!blip.blockAnchorId) continue;
      counts.set(
        blip.blockAnchorId,
        (counts.get(blip.blockAnchorId) ?? 0) + 1,
      );
    }
    return counts;
  }, [legacyBlips]);

  const flushBlockSync = useCallback(
    (blockId: string) => {
      const existing = textSyncTimers.current.get(blockId);
      if (existing) clearTimeout(existing);
      textSyncTimers.current.delete(blockId);
      const latest = bridge.blocks.find((b) => b.id === blockId);
      if (latest) onEmitOps(bridge.encodeUpdateBlockOp(latest));
    },
    [bridge, onEmitOps],
  );

  const applyBlockContentUpdate = useCallback(
    (updated: import("@vida/wave-core").WaveBlock) => {
      bridge.updateBlock(updated);
      const blockId = updated.id;
      const existing = textSyncTimers.current.get(blockId);
      if (existing) clearTimeout(existing);
      textSyncTimers.current.set(
        blockId,
        setTimeout(() => {
          textSyncTimers.current.delete(blockId);
          const latest = bridge.blocks.find((b) => b.id === blockId);
          if (latest) onEmitOps(bridge.encodeUpdateBlockOp(latest));
        }, TEXT_SYNC_DEBOUNCE_MS),
      );
    },
    [bridge, onEmitOps],
  );

  const insertBlock = useCallback(
    (kind: WaveBlockKind) => {
      const afterId = resolveInsertAnchor();
      if (kind === WaveBlockKind.File) {
        fileInputRef.current?.click();
        setCommandText("");
        clearInlineInsert();
        return;
      }
      if (editor) {
        const block = editor.appendBlock(kind, afterId ?? null);
        stampBlockAuthor(editor, block.id, userSub, wave.participants);
        if (kind === WaveBlockKind.TodoList) {
          editor.setMetaString(block.id, "projectId", wave.spaceId);
          editor.addTodoItem(block.id, userSub);
        }
        syncAfterYjsEdit();
        setCommandText("");
        clearInlineInsert();
        return;
      }
      const block = createDefaultBlock(kind);
      onEmitOps(bridge.insertBlock(block, afterId ?? undefined));
      setCommandText("");
      clearInlineInsert();
    },
    [
      bridge,
      clearInlineInsert,
      editor,
      onEmitOps,
      resolveInsertAnchor,
      syncAfterYjsEdit,
      userSub,
      wave.participants,
      wave.spaceId,
    ],
  );

  const attachSummaryBlock = useCallback(
    (parentId: string, text: string) => {
      if (editor) {
        const block = editor.attachBlock(parentId, WaveBlockKind.Text, "summary");
        stampBlockAuthor(editor, block.id, userSub, wave.participants);
        editor.setText(block.id, "text", text);
        syncAfterYjsEdit();
        return;
      }
      const block = createDefaultBlock(WaveBlockKind.Text);
      block.payload.text = text;
      block.payload.parentId = parentId;
      block.payload.relation = "summary";
      onEmitOps(bridge.insertBlock(block, undefined));
    },
    [bridge, editor, onEmitOps, syncAfterYjsEdit, userSub, wave.participants],
  );

  const addPlainTextFromField = useCallback(() => {
    const trimmed = commandText.trim();
    if (!trimmed || trimmed.startsWith("/")) return;
    const afterId = resolveInsertAnchor();
    if (editor) {
      const block = editor.appendBlock(WaveBlockKind.Text, afterId ?? null);
      stampBlockAuthor(editor, block.id, userSub, wave.participants);
      editor.setText(block.id, "text", trimmed);
      syncAfterYjsEdit();
      setCommandText("");
      clearInlineInsert();
      return;
    }
    const block = createDefaultBlock(WaveBlockKind.Text);
    block.payload.text = trimmed;
    onEmitOps(bridge.insertBlock(block, afterId ?? undefined));
    setCommandText("");
    clearInlineInsert();
  }, [
    commandText,
    bridge,
    clearInlineInsert,
    editor,
    onEmitOps,
    resolveInsertAnchor,
    syncAfterYjsEdit,
    wave.participants,
  ]);

  const insertAssistantBlock = useCallback(async () => {
    const docQuery = await runWaveDocQueryFromDirective(wave.waveId, commandText);
    if (docQuery != null) {
      setDocQueryBusy(true);
      setCommandText("");
      const parentFileId = resolveDocQueryParentFile(liveBlocks);
      try {
        if (parentFileId) {
          attachSummaryBlock(parentFileId, docQuery.trim());
        } else if (editor) {
          const afterId = resolveInsertAnchor();
          const block = editor.appendBlock(WaveBlockKind.Text, afterId ?? null);
          stampBlockAuthor(editor, block.id, userSub, wave.participants);
          editor.setText(block.id, "text", docQuery.trim());
          syncAfterYjsEdit();
        } else {
          const block = createDefaultBlock(WaveBlockKind.Text);
          block.payload.text = docQuery.trim();
          onEmitOps(bridge.insertBlock(block, resolveInsertAnchor() ?? undefined));
        }
        trackActivity("Documenti Wave", "Risposta da allegati indicizzati.");
        showWaveNotification("Documenti Wave", "Risposta pronta nel thread.");
      } catch (e) {
        trackActivity(
          "Documenti Wave",
          e instanceof Error ? e.message : "Errore",
        );
      } finally {
        setDocQueryBusy(false);
        clearInlineInsert();
      }
      return;
    }

    const instruction = commandText
      .replace(/@assistente/gi, "")
      .trim();
    const afterId = resolveInsertAnchor();
    if (editor) {
      const block = editor.appendBlock(WaveBlockKind.Text, afterId ?? null);
      stampBlockAuthor(editor, block.id, userSub, wave.participants);
      editor.setText(block.id, "text", `⏳ Assistente: ${instruction}`);
      syncAfterYjsEdit();
      setCommandText("");
    } else {
      const block = createDefaultBlock(WaveBlockKind.Text);
      block.payload.text = `⏳ Assistente: ${instruction}`;
      onEmitOps(bridge.insertBlock(block, afterId ?? undefined));
      setCommandText("");
    }
    clearInlineInsert();
    try {
      const { jobId } = await runWaveAssistant(wave.waveId, instruction);
      trackActivity("Assistente Wave", `Job ${jobId} in coda`);
      showWaveNotification("Assistente Wave", "Job inviato in coda.");
    } catch (e) {
      trackActivity(
        "Assistente Wave",
        e instanceof Error ? e.message : "Errore",
      );
    }
  }, [
    attachSummaryBlock,
    clearInlineInsert,
    commandText,
    bridge,
    editor,
    liveBlocks,
    onEmitOps,
    resolveInsertAnchor,
    syncAfterYjsEdit,
    wave.participants,
    wave.waveId,
  ]);

  const uploadFiles = useCallback(
    async (files: FileList | File[]) => {
      if (!editor || showPlayback) return;
      const list = Array.from(files);
      const imageFiles = list.filter((f) => inferBlobKind(f) === "image");
      const otherFiles = list.filter((f) => inferBlobKind(f) !== "image");

      if (imageFiles.length > 0) {
        const galleryId = resolveGalleryBlockId(
          editor,
          liveBlocks,
          resolveInsertAnchor(),
        );
        await uploadImagesToGalleryBlock(editor, waveId, galleryId, imageFiles, (msg) => {
          trackActivity("Galleria", msg);
        });
        trackActivity(
          "Galleria",
          imageFiles.length === 1
            ? imageFiles[0]!.name
            : `${imageFiles.length} immagini`,
        );
      }

      for (const file of otherFiles) {
        const afterId = resolveInsertAnchor();
        const block = editor.appendBlock(WaveBlockKind.File, afterId ?? null);
        stampBlockAuthor(editor, block.id, userSub, wave.participants);
        const kind = inferBlobKind(file);
        const mime = normalizedContentType(file, kind);
        editor.setMetaString(block.id, "fileName", file.name);
        editor.setMetaString(block.id, "mimeType", mime);
        editor.setMetaInt(block.id, "fileSize", file.size);
        editor.setMetaString(block.id, "uploadStatus", "uploading");
        editor.setMetaString(block.id, "ragStatus", "none");
        try {
          const result = await uploadBlobFile(file, "wave", {
            waveId,
            spaceId: wave.spaceId,
            blockId: block.id,
            mimeType: mime,
          });
          editor.setMetaString(block.id, "s3Key", result.s3Key);
          editor.setMetaString(block.id, "uploadStatus", "ready");
          editor.setMetaString(
            block.id,
            "ragStatus",
            isIndexableMime(mime, file.name) ? "pending" : "none",
          );
          trackActivity("Allegati", file.name);
        } catch (e) {
          editor.setMetaString(block.id, "uploadStatus", "error");
          trackActivity(
            "Allegati",
            e instanceof Error ? e.message : "Upload fallito",
          );
        }
      }
      syncAfterYjsEdit();
      clearInlineInsert();
    },
    [
      clearInlineInsert,
      editor,
      liveBlocks,
      resolveInsertAnchor,
      showPlayback,
      syncAfterYjsEdit,
      waveId,
      userSub,
      wave.participants,
    ],
  );

  const onDragOverEditor = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (showPlayback || !editor) return;
    e.preventDefault();
    setDragOver(true);
  }, [editor, showPlayback]);

  const onDragLeaveEditor = useCallback(() => setDragOver(false), []);

  const onDropEditor = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (showPlayback || !editor) return;
      e.preventDefault();
      setDragOver(false);
      void (async () => {
        const prepared = await filesFromDataTransfer(e.dataTransfer);
        if (prepared.length > 0) {
          void uploadFiles(prepared);
        }
      })();
    },
    [editor, showPlayback, uploadFiles],
  );

  const togglePlayback = useCallback(async () => {
    if (showPlayback) {
      setShowPlayback(false);
      return;
    }
    onPlaybackBegin?.();
    await onRefreshPlayback();
    setPlaybackSeq(0);
    setShowPlayback(true);
  }, [showPlayback, onRefreshPlayback, onPlaybackBegin]);

  const renderBlockSection = (block: (typeof displayedBlocks)[number]) => {
    const agentTasks = displayedBlocks.filter(
      (b) => b.payload.parentId === block.id && b.payload.relation === "agent_task",
    );
    const linkedAgentTask =
      agentTasks.length > 0
        ? agentTasks.reduce((latest, b) =>
            (b.sortIndex ?? 0) >= (latest.sortIndex ?? 0) ? b : latest,
          )
        : null;
    const commentCount = usesYjsComments
      ? (commentCountByBlock.get(block.id) ?? 0)
      : (legacyBlipCountByBlock.get(block.id) ?? 0);
    const isNestedChild = Boolean(block.payload.parentId);
    const isCollapsed = collapsedBlocks.has(block.id);
    const nestedChildCount = nestedCounts.get(block.id) ?? 0;
    const nestedChildIds =
      nestedChildCount > 0 ? nestedBlockIdsForParent(displayedBlocks, block.id) : [];
    const allNestedCollapsed =
      nestedChildIds.length > 0 && nestedChildIds.every((id) => collapsedBlocks.has(id));
    const commentsPanelOpen =
      !isCollapsed &&
      (commentCount > 0
        ? !commentsCollapsedBlocks.has(block.id)
        : selectedBlipBlockId === block.id);
    const collapseComments = () => {
      setSelectedBlipBlockId(null);
      if (commentCount > 0) {
        setCommentsCollapsedBlocks((prev) => new Set(prev).add(block.id));
      }
    };
    return (
      <div
        key={block.id}
        className={`wave-feed-block-wrap${commentCount > 0 ? " wave-feed-block-wrap--has-comments" : ""}${isCollapsed ? " wave-feed-block-wrap--collapsed" : ""}`}
      >
        <WaveBlockView
          waveId={waveId}
          spaceId={wave.spaceId}
          block={block}
          collapsible
          collapseMode={isNestedChild ? "stub" : "soft"}
          collapsed={isCollapsed}
          onToggleCollapse={() => toggleBlockCollapse(block.id)}
          nestedChildCount={!isNestedChild ? nestedChildCount : undefined}
          nestedHidden={!isNestedChild && nestedHiddenBlocks.has(block.id)}
          onToggleNested={
            !isNestedChild && nestedChildCount > 0
              ? () => toggleNestedHidden(block.id)
              : undefined
          }
          allNestedCollapsed={!isNestedChild ? allNestedCollapsed : undefined}
          onToggleCollapseAllNested={
            !isNestedChild && nestedChildCount > 0
              ? () => toggleCollapseAllNested(block.id)
              : undefined
          }
          editor={showPlayback ? null : editor}
          currentUserSub={userSub}
          participants={wave.participants}
          mentionParticipants={mentionParticipants}
          comments={liveComments}
          remoteContentRevision={contentRevision}
          readOnly={showPlayback}
          blipCount={commentCount}
          remoteEditors={editorsOnBlock(presences, block.id, userSub)}
          onBeginEditing={() => beginEditing(block.id)}
          onTypingActivity={() => touchTypingActivity(block.id)}
          onEndEditing={() => {
            if (editor) void syncAfterYjsEdit();
            else flushBlockSync(block.id);
            endEditing();
          }}
          onSelectionChange={(from, to) => publishCursor(block.id, from, to)}
          showsBlipThread={commentsPanelOpen}
          onPostTodoComment={
            showPlayback || !usesYjsComments
              ? undefined
              : (anchor, body, parentId) => onPostComment!(anchor, body, parentId, null)
          }
          onPostGalleryComment={
            showPlayback || !usesYjsComments
              ? undefined
              : (anchor, body, parentId, galleryItemId) =>
                  onPostComment!(anchor, body, parentId, galleryItemId)
          }
          onToggleReaction={
            showPlayback ? undefined : (commentId, emoji) => onToggleReaction?.(commentId, emoji)
          }
          onAfterTodoEdit={showPlayback ? undefined : syncAfterYjsEdit}
          onArchive={
            showPlayback || !editor ? undefined : handleArchiveBlock
          }
          onOpenAnalyst={
            showPlayback || !editor ? undefined : handleOpenAnalyst
          }
          onAskWhale={showPlayback || !editor ? undefined : handleAskWhale}
          linkedAgentTask={linkedAgentTask}
          onUpdate={showPlayback || editor ? undefined : applyBlockContentUpdate}
          onVote={
            showPlayback || editor
              ? undefined
              : (choice) => {
                  const b = bridge.blocks.find((x) => x.id === block.id);
                  if (!b) return;
                  const votes = { ...(b.payload.votes ?? {}) };
                  votes[userSub] = choice;
                  onEmitOps(
                    bridge.updateBlock({
                      ...b,
                      payload: { ...b.payload, votes },
                    }),
                  );
                }
          }
          onApprove={
            showPlayback || editor
              ? undefined
              : () => {
                  const b = bridge.blocks.find((x) => x.id === block.id);
                  if (!b) return;
                  const approvals = { ...(b.payload.approvals ?? {}) };
                  approvals[userSub] = true;
                  onEmitOps(
                    bridge.updateBlock({
                      ...b,
                      payload: { ...b.payload, approvals },
                    }),
                  );
                }
          }
          onToggleTask={
            showPlayback || editor
              ? undefined
              : () => {
                  const b = bridge.blocks.find((x) => x.id === block.id);
                  if (!b) return;
                  onEmitOps(
                    bridge.updateBlock({
                      ...b,
                      payload: {
                        ...b.payload,
                        taskDone: !(b.payload.taskDone ?? false),
                      },
                    }),
                  );
                }
          }
          onToggleBlips={
            showPlayback && commentCount === 0
              ? undefined
              : () => {
                  if (commentCount > 0) {
                    setCommentsCollapsedBlocks((prev) => {
                      const next = new Set(prev);
                      if (next.has(block.id)) {
                        next.delete(block.id);
                        if (!showPlayback) beginEditing(block.id);
                      } else {
                        next.add(block.id);
                        setSelectedBlipBlockId(null);
                      }
                      return next;
                    });
                    return;
                  }
                  setSelectedBlipBlockId((current) => {
                    const opening = current !== block.id;
                    if (!showPlayback && opening) beginEditing(block.id);
                    return opening ? block.id : null;
                  });
                }
          }
        />
        {commentsPanelOpen && usesYjsComments && (
          <div className="wave-feed-block-comments">
            <WaveCommentThread
              comments={liveComments}
              blockAnchorId={block.id}
              participants={wave.participants}
              mentionParticipants={mentionParticipants}
              presences={presences}
              currentUserSub={userSub}
              readOnly={showPlayback}
              galleryItems={
                block.kind === WaveBlockKind.Gallery
                  ? (block.payload.galleryItems ?? [])
                  : undefined
              }
              onPost={(body, parentId) =>
                onPostComment!(block.id, body, parentId, null)
              }
              onToggleReaction={(commentId, emoji) =>
                onToggleReaction?.(commentId, emoji)
              }
              onCollapse={collapseComments}
            />
          </div>
        )}
        {commentsPanelOpen && !usesYjsComments && (
          <div className="wave-feed-block-comments">
          <WaveBlipThread
            blips={legacyBlips}
            blockAnchorId={block.id}
            participants={wave.participants}
            draft={blipDraft}
            readOnly={showPlayback || !onPostLegacyBlip}
            onDraftChange={setBlipDraft}
            onPost={() => {
              const body = blipDraft.trim();
              if (!body || !onPostLegacyBlip) return;
              onPostLegacyBlip(block.id, body);
              setBlipDraft("");
            }}
            onCollapse={collapseComments}
          />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="wave-editor" ref={editorRootRef}>
      {showToc && (
        <WaveThreadToc
          entries={tocEntries}
          activeBlockId={activeTocBlockId}
          dimmedBlockIds={tocDimmedIds}
          onSelect={handleTocSelect}
        />
      )}
      <div className="wave-editor-frame">
        <div className="wave-editor-canvas">
          <WaveEditorHeaderBar
            wave={wave}
            isFavorite={isFavorite}
            showPlayback={showPlayback}
            presences={presences}
            lastSeen={lastSeen}
            currentUserSub={userSub}
            currentUserEmail={currentUserEmail()}
            participants={wave.participants}
            onToggleFavorite={() => onToggleFavorite?.()}
            onRename={() => onRenameTitle?.()}
            onArchiveToggle={() => onArchiveToggle?.()}
            onStatusChange={onStatusChange}
            onTogglePlayback={() => void togglePlayback()}
            onInvite={() => onInvite?.()}
            onCoverKeyChange={onCoverKeyChange}
            onBack={onBack}
            backLabel={backLabel}
            sidebarCollapsed={sidebarCollapsed}
            onReopenSidebar={onReopenSidebar}
            threadView={effectiveThreadView}
            onThreadViewChange={handleThreadViewChange}
            timelineOrder={effectiveTimelineOrder}
            onTimelineOrderToggle={handleTimelineOrderToggle}
            archivedCount={archivedBlocks.length}
            onShowArchivedWidgets={() => setArchivedPanelOpen(true)}
            allCollapsed={allTopLevelCollapsed}
            onToggleCollapseAll={handleToggleCollapseAll}
            tocVisible={tocVisible}
            onToggleToc={handleToggleToc}
          />

          {showPlayback && (
            <div className="wave-editor-playback-wrap">
              <WavePlaybackView
                currentSeq={playbackSeq}
                maxSeq={playbackMaxSeq}
                previewBlocks={displayedBlocks}
                onSeqChange={setPlaybackSeq}
              />
            </div>
          )}

          <div
            className={`wave-blocks wave-timeline-feed-wrap${dragOver ? " wave-blocks--drag-over" : ""}`}
            onDragOver={onDragOverEditor}
            onDragLeave={onDragLeaveEditor}
            onDrop={onDropEditor}
          >
            {dragOver && (
              <p className="wave-drop-hint">Rilascia i file per allegarli al thread</p>
            )}
            {showPlayback && (
              <p className="playback-hint">
                Modalità playback — usa lo slider per scorrere la cronologia.
              </p>
            )}
            <WaveTimelineFeed
              blocks={displayedBlocks}
              view={effectiveThreadView}
              timelineOrder={effectiveTimelineOrder}
              hiddenParentIds={nestedHiddenBlocks}
              density={feedDensity}
              participants={wave.participants}
              remoteEditorsForBlock={(block) => editorsOnBlock(presences, block.id, userSub)}
              authorForBlock={(block) =>
                resolveFeedAuthor(block, wave.participants, feedAuthorContext)
              }
              showInsertAffordance={effectiveThreadView === "story" && !showPlayback}
              onInsertAfter={openInlineInsert}
            >
              {(row) => renderBlockSection(row.block)}
            </WaveTimelineFeed>
          </div>

          {!showPlayback && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  if (e.target.files?.length) {
                    void uploadFiles(e.target.files);
                  }
                  e.target.value = "";
                }}
              />
              <WaveTimelineComposer
                commandText={commandText}
                onCommandChange={setCommandText}
                onAddPlainText={addPlainTextFromField}
                onAddAssistant={() => void insertAssistantBlock()}
                onPickFile={() => fileInputRef.current?.click()}
                onInsertBlock={insertBlock}
                busy={docQueryBusy}
              />
            </>
          )}

          {inlinePickerOpen && (
            <div
              className="wave-inline-widget-picker-backdrop"
              role="presentation"
              onClick={clearInlineInsert}
            >
              <div
                className="wave-inline-widget-picker-dialog"
                role="dialog"
                aria-label="Inserisci widget"
                onClick={(e) => e.stopPropagation()}
              >
                <WaveWidgetPicker
                  commandText={commandText}
                  onAddPlainText={() => {
                    addPlainTextFromField();
                    clearInlineInsert();
                  }}
                  onAddAssistant={() => {
                    void insertAssistantBlock();
                    clearInlineInsert();
                  }}
                  onPickFile={() => {
                    fileInputRef.current?.click();
                    clearInlineInsert();
                  }}
                  onInsertBlock={(kind) => insertBlock(kind)}
                />
                <button
                  type="button"
                  className="wave-inline-widget-picker-dismiss"
                  onClick={clearInlineInsert}
                >
                  Annulla
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {archivedPanelOpen && (
        <WaveArchivedWidgetsPanel
          blocks={archivedBlocks}
          onRestore={handleRestoreBlock}
          onDeleteForever={handleDeleteBlockForever}
          onClose={() => setArchivedPanelOpen(false)}
        />
      )}

      {analystTarget && (
        <div className="wave-analyst-layer">
          <WaveAnalystPanel
            waveId={waveId}
            target={analystTarget}
            editor={showPlayback ? null : editor}
            open={analystOpen}
            onOpenChange={setAnalystOpen}
            onSaved={() => void syncAfterYjsEdit()}
          />
        </div>
      )}

      {whaleTarget && (
        <div className="wave-whale-layer">
          <WaveWhalePanel
            waveId={waveId}
            spaceId={wave.spaceId}
            target={whaleTarget}
            editor={showPlayback ? null : editor}
            launchedBy={userSub}
            open={whaleOpen}
            onOpenChange={setWhaleOpen}
            onSaved={() => void syncAfterYjsEdit()}
          />
        </div>
      )}
    </div>
  );
}
